import styled from "styled-components";
import {DialogContent} from "@mui/material";
import {radii} from "theme/tokens";

/**
 * Externalized styles for {@link ./TemplatePreviewLauncher} (ui#588).
 *
 * The picker card is a `<label>` wrapping a hidden radio, so clicking ANYWHERE in it selects the
 * treatment. That is the behaviour the whole picker is built on, and it is why the view-larger control
 * cannot live inside the card: a control nested in a label is a control whose click has two meanings, and
 * the one the owner asked for ("do NOT overload tile click") is the one that would lose.
 *
 * {@link TemplateOption} is the seam that makes both possible — a positioning parent the label fills and
 * the button floats over. The button is a SIBLING of the label rather than a descendant, so a click on it
 * is not a click on the label under any spec reading, in any engine, at any point in the future.
 */

/**
 * One option in the picker grid: the selectable card, and the controls that float over it.
 *
 * `display: grid` rather than `block` so the single child stretches to the row's height. The cards used to
 * be the grid items themselves and were stretched by {@link PickerGrid}; wrapping them without this would
 * leave every card the height of its own text and break the row's alignment.
 */
export const TemplateOption = styled.div`
	position: relative;
	display: grid;
`;

/**
 * The view-larger control: an icon button in the preview's top-right corner.
 *
 * Sized to the 32px touch target the rest of the account surfaces use rather than to the icon, and given
 * an opaque card background because it sits ON the sponsor's own artwork — an arbitrary photograph, which
 * may be any colour. A translucent chip over a light plate is an invisible control.
 *
 * Deliberately NOT hidden until hover: the owner asked for an affordance a viewer can find, and a control
 * that only exists once the pointer is already on it does not exist for a touch reader at all.
 */
export const ViewLargerButton = styled.button`
	position: absolute;
	z-index: 2;
	top: 8px;
	right: 8px;
	width: 32px;
	height: 32px;
	display: grid;
	place-items: center;
	padding: 0;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	transition: 0.16s;

	&:hover {
		border-color: ${({theme}) => theme.tokens.blue};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** The dialog body. Padded like every other {@link BaseDialog} consumer, and it never scrolls. */
export const PreviewBody = styled(DialogContent)`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

/**
 * The showcase stage the REAL treatment animates inside.
 *
 * Positioned and clipped because `SponsorTakeoverArt` mounts an `inset: 0` absolute layer and expects an
 * ancestor exactly like this — the download page's `PageBackdrop` is its counterpart in production, and
 * the picker's own `PreviewStage` is the miniature one.
 *
 * 16:9 rather than the tile's 16:10, and capped by the VIEWPORT's height as well as by the dialog's width.
 * A dialog that is taller than the window puts its own close button off-screen, and this body deliberately
 * does not scroll: an art layer inside a scrolling region would give the hero a scroll relationship that
 * the recipient page's hero has and this preview's must not, since the sweep is already driving it.
 */
export const ShowcaseStage = styled.div`
	position: relative;
	overflow: hidden;
	aspect-ratio: 16 / 9;
	max-height: 62vh;
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.line};
`;

/** The treatment's own description, repeated under the art so the overlay says what it is showing. */
export const PreviewCaption = styled.p`
	margin: 0;
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13.5px;
	line-height: 1.5;
`;
